import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import db from "../db/database";
import { getCountryData } from "../utils/countryUtils";

const ConfigContext = createContext();

// SOLO PARA PRUEBAS LOCALES: permite que el país activo real (ej. Ecuador)
// se comporte en toda la app como si fuera otro país (ej. Perú) -- sin tocar
// el entorno/BC/TPV/usuario reales, que siguen siendo los del país verdadero.
// Todo lo que lee country.code vía useConfig() (configs por país, RBAC,
// extensiones, formato de moneda) queda afectado, ya que este es el único
// punto donde se resuelve el país activo.
// Se activa con REACT_APP_COUNTRY_OVERRIDE en .env.local (requiere reiniciar
// npm start -- las variables de entorno de CRA se incrustan en el build).
// NUNCA debe estar seteada en un build de producción.
const COUNTRY_OVERRIDE = process.env.REACT_APP_COUNTRY_OVERRIDE || null;
if (COUNTRY_OVERRIDE) {
  console.warn(
    `⚠️ COUNTRY_OVERRIDE activo: el país se está simulando como "${COUNTRY_OVERRIDE}" (REACT_APP_COUNTRY_OVERRIDE en .env.local). Quitar antes de producción.`
  );
}
export const resolveEffectiveCountryCode = (realCountryCode) => COUNTRY_OVERRIDE || realCountryCode;

export const AppConfigProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [activeEnv, setActiveEnv] = useState(null);
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);

  // Carga la configuración activa
  useEffect(() => {
    const loadActiveEnv = async () => {
      setLoading(true);
      try {
        const env = await db.environment.filter((e) => e.isActive === true).first();
        if (env) {
          setActiveEnv(env);
          setCountry(getCountryData(resolveEffectiveCountryCode(env.country)));
        }
      } catch (error) {
        console.error("Error al cargar configuración:", error);
      } finally {
        setLoading(false);
      }
    };
    loadActiveEnv();
  }, [currentUser]);

  // Cambiar de entorno (y actualizar en Dexie)
  const setActiveEnvironment = async (envId) => {
    await db.transaction("rw", db.environment, async () => {
      await db.environment.toCollection().modify({ isActive: false });
      await db.environment.update(envId, { isActive: true });
      const newEnv = await db.environment.get(envId);
      setActiveEnv(newEnv);
      setCountry(getCountryData(resolveEffectiveCountryCode(newEnv.country)));
    });
  };

  return (
    <ConfigContext.Provider
      value={{
        loading,
        activeEnv, // toda la config activa
        country, // objeto con info del país (moneda, símbolo, etc.)
        setActiveEnvironment,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => useContext(ConfigContext);
